import { ApsBidder } from '@tunein/web-common';
import { omit } from 'es-toolkit';
import { forEach } from 'es-toolkit/compat';
import { v4 as uuidv4 } from 'uuid';
import { AB_TEST_IDS, ADS_PERSONA, ADS_PREROLL_CAP_COUNT_ENABLED, ADS_SMART_PREROLL_ENABLED, } from '../constants';
import { appSessionDataProvider, localizationsProvider } from '../providers';
import { api, player } from '../singletons';
import { debug } from '../utils';
import { buildAdsWizzAdTagUrl, buildGoogleIMAAdTagUrl } from '../utils/adTagBuilder';
import { AdReporter } from './AdReporter';
import { providerToAdNetworkMap } from './AdReporter/constants';
import { adTypes, CAP_KEY_BY_MEDIUM, clientAdTypes, FREESTAR_ENABLED, GUARANTEE_MIDROLL, GUARANTEE_MIDROLL_CHROMECAST, GUARANTEE_PREROLL, GUARANTEE_PREROLL_CHROMECAST, MIDROLL_ENABLED, PREROLL_ENABLED, VIDEO_PREROLL_ENABLED, VIDEO_TAM_ENABLED, VIDEO_TAM_READY_TIMEOUT, VIDEO_TAM_REFETCH_TIMEOUT, } from './constants';
import { MidrollScheduler } from './MidrollScheduler';
export class AdPlayer {
    #adReporter;
    #streamDataManager;
    #midrollScheduler;
    #playerEvents;
    #sessionConfig;
    #isFirstInSession = true;
    #apsBidder;
    #didVideoPrerollPlay = false;
    #isReturningFromBoost = false;
    #adBreakSessionId = null;
    #currentAdMedium = null;
    #startedPrerollsCount = 0;
    #wasAdCancelled = false;
    constructor(options) {
        const { ssaiManager, streamDataManager } = options;
        this.#streamDataManager = streamDataManager;
        this.#playerEvents = {
            playing: this.#onPlaying,
            pause: this.#onPause,
            emptied: this.#onStopped,
            ended: this.#onStopped,
            waiting: this.#onPause,
            error: this.#onSourceError,
        };
        this.#adReporter = new AdReporter({ ssaiManager });
        this.#apsBidder = new ApsBidder({
            areVideoBidsEnabled: api.getConfigSetting(VIDEO_TAM_ENABLED),
            videoBidsFetchIntervalMinutes: api.getConfigSetting(VIDEO_TAM_REFETCH_TIMEOUT),
            videoBidsFetchTimeoutMs: api.getConfigSetting(VIDEO_TAM_READY_TIMEOUT),
        });
        this.#registerPlayerEvents();
        this.#init();
    }
    get #currentStream() {
        return (player.currentSource || {});
    }
    get #nowPlayingAdsSettings() {
        const { nowPlaying } = this.#sessionConfig;
        return nowPlaying?.ads || {};
    }
    get #canPlayPreroll() {
        if (!this.canPlayClientSideAds) {
            return false;
        }
        const { canShowAds } = this.#nowPlayingAdsSettings;
        const canPlayAudioOrVideoPreroll = this.#canPlayAudioPreroll || this.#canPlayVideoPreroll;
        const canPlayPreroll = canShowAds && canPlayAudioOrVideoPreroll && api.getConfigSetting(PREROLL_ENABLED);
        // QA controls. See constant definitions for more details.
        const forcePreroll = api.getConfigSetting(GUARANTEE_PREROLL) || api.getConfigSetting(GUARANTEE_PREROLL_CHROMECAST);
        return Boolean(canPlayPreroll || forcePreroll);
    }
    get #canPlayAudioPreroll() {
        const { canShowPrerollAds } = this.#nowPlayingAdsSettings;
        const audioAdUnitId = api.getAdUnitConfig('preroll', 'audio')?.adUnitId;
        if (!audioAdUnitId) {
            this.#adReporter.reportNoAdConfig();
        }
        return Boolean(audioAdUnitId && canShowPrerollAds);
    }
    get #canPlayVideoPreroll() {
        const videoAdUnitId = api.getAdUnitConfig('preroll', 'video')?.adUnitId;
        const { canShowVideoPrerollAds } = this.#nowPlayingAdsSettings;
        if (!videoAdUnitId) {
            this.#adReporter.reportNoAdConfig();
        }
        return Boolean(videoAdUnitId && api.getConfigSetting(VIDEO_PREROLL_ENABLED) && canShowVideoPrerollAds);
    }
    get #canPlayMidroll() {
        if (!this.canPlayClientSideAds) {
            return false;
        }
        const { canShowAds, canShowPrerollAds } = this.#nowPlayingAdsSettings;
        const midrollAdUnitId = api.getAdUnitConfig('midroll', 'audio')?.adUnitId;
        // Give precedence to the adConfig and player status over the nowPlaying data and config settings
        if (!midrollAdUnitId || !player.isPlaying()) {
            return false;
        }
        const canPlayMidroll = canShowAds &&
            canShowPrerollAds && // the NowPlaying API doesn't have a `canShowMidrollAds`, so we rely on `canShowPrerollAds`
            api.getConfigSetting(MIDROLL_ENABLED) &&
            this.#currentStream.isMidrollEnabled &&
            !this.#currentStream.isSSAI; // SSAI streams embed their own instream midrolls
        return Boolean(canPlayMidroll ||
            api.getConfigSetting(GUARANTEE_MIDROLL) ||
            api.getConfigSetting(GUARANTEE_MIDROLL_CHROMECAST));
    }
    async #init() {
        if (this.canPlayClientSideAds) {
            await appSessionDataProvider.ready;
            player.setOptions({ advertising: appSessionDataProvider.adOptions });
            this.#initializeApsBidder();
            return;
        }
        // Check again on auth change, in case we need to initialize ads later
        api.on('authTokensUpdated', this.#onAuthTokensUpdated);
    }
    #registerPlayerEvents() {
        forEach(this.#playerEvents, (cb, event) => {
            player.on(event, cb);
        });
    }
    #deregisterPlayerEvents() {
        forEach(this.#playerEvents, (cb, event) => player.off(event, cb));
    }
    #registerAdsEvents() {
        player.on('adimpression', this.#onAdImpression);
        const { prerollTimeCapDuration, prerollCountCapEnabled } = this.#getAdsLimits();
        // Preroll time capping
        if (prerollTimeCapDuration > 0) {
            player.on('adprogress', this.#onAdProgress);
        }
        // Preroll count capping
        if (prerollCountCapEnabled) {
            player.on('adloaded', this.#onAdLoaded);
        }
    }
    #deregisterAdsEvents() {
        player.off('adprogress', this.#onAdProgress);
        player.off('adloaded', this.#onAdLoaded);
        player.off('adimpression', this.#onAdImpression);
    }
    #onPlaying = () => {
        this.#midrollScheduler?.start();
    };
    #onPause = () => {
        this.#midrollScheduler?.pause();
    };
    #onStopped = () => {
        this.#midrollScheduler?.clear();
    };
    #onSourceError = (event) => {
        if (event.error.fatal) {
            this.#midrollScheduler?.clear();
        }
    };
    #onAuthTokensUpdated = () => {
        if (this.canPlayClientSideAds) {
            api.off('authTokensUpdated', this.#onAuthTokensUpdated);
            this.#init();
        }
    };
    #onAdProgress = (event) => {
        const { currentTime } = event;
        const { prerollTimeCapDuration } = this.#getAdsLimits();
        if (!currentTime || !this.#currentAdMedium || prerollTimeCapDuration <= 0) {
            return;
        }
        if (currentTime > prerollTimeCapDuration) {
            player.forceSkipAd();
            this.#wasAdCancelled = true;
            this.#adReporter.reportPlaybackCancelled({ duration: currentTime, reason: 'time' });
        }
    };
    #onAdLoaded = () => {
        const { maxTotalPrerollAds, prerollCountCapEnabled } = this.#getAdsLimits();
        // We are checking startedPrerollsCount to be larger or EQUAL because impression is triggered after adloaded event
        if (prerollCountCapEnabled && this.#startedPrerollsCount >= maxTotalPrerollAds) {
            // Note: It would be better to call here adsManager.discardAdBreak() but it has no effect on AdsWizz ads (reported)
            player.forceSkipAd();
            this.#wasAdCancelled = true;
            this.#adReporter.reportPlaybackCancelled({ reason: 'count' });
        }
    };
    #onAdImpression = () => {
        this.#startedPrerollsCount += 1;
    };
    #setAdBreak(isAdBreak) {
        player.isAdBreak = isAdBreak;
        this.#streamDataManager.setOutstreamAdBreak(isAdBreak);
    }
    #initializeApsBidder() {
        const { gdprApplies, gdprTcString, usPrivacyString } = appSessionDataProvider.consentData;
        const isFreestarEnabled = api.getConfigSetting(FREESTAR_ENABLED, false);
        this.#apsBidder.init({
            useApsIframe: isFreestarEnabled,
            consentData: {
                gdpr: {
                    gdprApplies: gdprApplies || false,
                    tcString: gdprTcString,
                },
                ccpa: {
                    usPrivacyString,
                },
            },
        });
        this.#apsBidder.on('bidsRequested', this.#adReporter.reportAudioVideoAdsBiddingRequested);
        this.#apsBidder.on('bidsResponseReceived', this.#adReporter.reportAudioVideoAdsBiddingResponseReceived);
        this.#apsBidder.on('bidsRequestFailed', this.#adReporter.reportAudioVideoAdsBiddingRequestFailed);
    }
    async #waitForServerSidePrerollEnd() {
        const { promise, resolve } = Promise.withResolvers();
        function onEnded() {
            resolve(true);
            player.off('ended', onEnded);
            player.off('error', onError);
        }
        function onError(event) {
            if (!(event.error?.fatal && event.isLastSource)) {
                return;
            }
            resolve(false);
            player.off('ended', onEnded);
            player.off('error', onError);
        }
        player.on('ended', onEnded);
        player.on('error', onError);
        return promise;
    }
    async #requestServerSidePreroll() {
        const wasAutoPlayEnabled = player.getOptions().autoPlay;
        // disable autoplay so that we can send ad reports at the appropriate times
        if (wasAutoPlayEnabled) {
            player.setOptions({ autoPlay: false });
        }
        this.#adReporter.registerEvents();
        this.#adReporter.reportServerSidePrerollRequested();
        await player.load(this.#sessionConfig.prerollStream);
        if (!player.isLoaded()) {
            this.#adReporter.deregisterEvents();
            if (wasAutoPlayEnabled) {
                player.setOptions({ autoPlay: true });
            }
            return false;
        }
        const prerollEndPromise = this.#waitForServerSidePrerollEnd();
        this.#adReporter.reportServerSidePrerollResponseReceived();
        player.play();
        const didAdPlay = await prerollEndPromise;
        this.#adReporter.deregisterEvents();
        if (wasAutoPlayEnabled) {
            player.setOptions({ autoPlay: true });
        }
        return didAdPlay;
    }
    #generateAdTagUrl(options) {
        const { adUnitId, medium, provider, maxAdsCount, companionSizes = '' } = options;
        const { guideId, nowPlaying } = this.#sessionConfig;
        const { env, currentScreen, consentData } = appSessionDataProvider;
        const { isTargetingEnabled, gdprApplies, gdprTcString, usPrivacyString } = consentData;
        const adsTargeting = isTargetingEnabled ? api.adsTargeting : omit(api.adsTargeting, ['ppid']);
        const commonRootParams = {
            us_privacy: usPrivacyString,
            ...(api.isInUS && { rdp: isTargetingEnabled ? '0' : '1' }),
        };
        const commonCustParams = {
            ...adsTargeting,
            lotamesegments: isTargetingEnabled ? consentData.audiences?.join(',') || '' : '',
            videoAdPlayed: this.#didVideoPrerollPlay.toString(),
            persona: api.getConfigSetting(ADS_PERSONA),
            abtest: api.getConfigSetting(AB_TEST_IDS),
            isLoggedIn: api.hasAuthTokens.toString(),
            screen: currentScreen,
            language: localizationsProvider.locale,
            env,
        };
        const isVideoAd = medium === 'video';
        if (provider === 'google') {
            const isSmartPrerollEnabled = api.getConfigSetting(ADS_SMART_PREROLL_ENABLED);
            const url = `https://tunein.com/desc/${guideId}/`;
            // Player will initially return undefined for volume, so we default to 1 (unmuted ads)
            const volume = player.getVolume() === undefined ? 1 : player.getVolume();
            const baseParams = {
                ...commonRootParams,
                url,
                iu: adUnitId,
                description_url: url,
                scor: guideId?.slice(1),
                vpmute: volume ? '0' : '1',
                npa: isTargetingEnabled ? '0' : '1',
                gdpr: gdprApplies ? '1' : '0',
                ciu_szs: companionSizes,
                isFirstInSession: this.#isFirstInSession?.toString(),
                pp: api.adsTargeting?.ads_partner_alias,
                us_privacy: usPrivacyString,
                ...(gdprApplies && gdprTcString && { gdpr_consent: gdprTcString }),
                cust_params: {
                    ...commonCustParams,
                    ...nowPlaying?.adTargeting,
                    partnerId: api.partnerId,
                    us_privacy: usPrivacyString,
                    ...(isSmartPrerollEnabled
                        ? { MaxReqAds: maxAdsCount.toString() }
                        : {
                            enableDoublePreroll: Boolean(this.#getAdsLimits().canShowDoublePrerollAds).toString(),
                        }),
                },
            };
            return buildGoogleIMAAdTagUrl({
                baseParams,
                isVideoAd,
                videoApsBids: isVideoAd ? this.#apsBidder.videoBidsAdParams : undefined,
            });
        }
        if (provider === 'adswizz') {
            const baseParams = {
                ...commonRootParams,
                partnerId: api.partnerId,
                maxAds: maxAdsCount.toString(),
                'aw_0_1st.playerid': api.partnerId,
                'aw_0_1st.isFirstInSession': this.#isFirstInSession?.toString(),
                ...(gdprApplies && {
                    'aw_0_req.userConsentV2': gdprTcString,
                    'aw_0_req.gdpr': Boolean(isTargetingEnabled).toString(),
                }),
            };
            const customParams = {
                ...commonCustParams,
                ...nowPlaying?.adTargeting,
            };
            return buildAdsWizzAdTagUrl({ baseParams, customParams, companionSizes });
        }
        return '';
    }
    async #requestAdBreak({ maxAdsCount, adType, medium }) {
        let playedAdsCount = 0;
        const { adUnitId, provider, companionSizes } = api.getAdUnitConfig(adType, medium) || {};
        if (!adUnitId || !provider) {
            this.#adReporter.reportNoAdConfig();
            return 0;
        }
        const adRequestId = uuidv4();
        const { primaryGuideId, secondaryGuideId } = this.#sessionConfig?.nowPlaying;
        this.#deregisterPlayerEvents();
        this.#registerAdsEvents();
        this.#adReporter.registerEvents();
        this.#adReporter.onAdRequested({
            type: adTypes.client,
            clientType: adType,
            adUnitId,
            adRequestId,
            guideIds: [primaryGuideId, secondaryGuideId].filter(Boolean).join(','),
            adNetworkName: providerToAdNetworkMap[provider],
            medium,
            adCount: this.#startedPrerollsCount,
            maxRolls: maxAdsCount,
            maxTotalRolls: this.#getAdsLimits().maxTotalPrerollAds,
            wasAdCancelled: this.#wasAdCancelled,
        });
        const adRequestUrl = this.#generateAdTagUrl({
            adUnitId,
            medium,
            provider,
            maxAdsCount,
            companionSizes,
        });
        debug('[AdPlayer] Generated ad tag URL:', adRequestUrl);
        // isFirstInSession is attached to the ad request url, so we should reset it now
        this.#isFirstInSession = false;
        try {
            this.#adReporter.reportAudioVideoAdRequested(Boolean(medium === 'video' && this.#apsBidder.hasVideoBids));
            playedAdsCount = await player.requestAdBreak(provider, adRequestId, adRequestUrl);
            if (!playedAdsCount) {
                debug(`[AdPlayer] No-fill for ${medium} ad break. Requested ${maxAdsCount} ads.`);
                this.#adReporter.reportNoFill();
            }
        }
        catch (error) {
            debug(`[AdPlayer] Error requesting ${medium} ad break:`, error);
            // AdErrorEvents are handled by AdReporter
            if (!error.isAdError) {
                this.#adReporter.reportAdError(error);
            }
        }
        this.#adReporter.deregisterEvents();
        this.#registerPlayerEvents();
        this.#deregisterAdsEvents();
        if (playedAdsCount && medium === 'video') {
            this.#didVideoPrerollPlay = true;
        }
        return playedAdsCount;
    }
    #getAdsLimits() {
        const { maxSwitchAudioPrerollAds = 1, maxTotalPrerollAds = 3, maxVideoPrerollAds = 2, canShowDoublePrerollAds, } = this.#nowPlayingAdsSettings;
        const isSmartPrerollEnabled = api.getConfigSetting(ADS_SMART_PREROLL_ENABLED);
        const prerollCountCapEnabled = api.getConfigSetting(ADS_PREROLL_CAP_COUNT_ENABLED, false);
        const prerollTimeCapDuration = this.#currentAdMedium
            ? api.getConfigSetting(CAP_KEY_BY_MEDIUM[this.#currentAdMedium], 0)
            : 0;
        // TODO: remove this once we're ready to permanently enable smart prerolls
        if (!isSmartPrerollEnabled) {
            return {
                maxTotalPrerollAds: canShowDoublePrerollAds ? 3 : 2,
                maxVideoPrerollAds: 1,
                canShowDoublePrerollAds,
                prerollTimeCapDuration,
                prerollCountCapEnabled,
            };
        }
        return {
            maxTotalPrerollAds: this.#isReturningFromBoost
                ? maxSwitchAudioPrerollAds
                : maxTotalPrerollAds,
            maxVideoPrerollAds: this.#isReturningFromBoost ? 0 : maxVideoPrerollAds,
            prerollTimeCapDuration,
            prerollCountCapEnabled,
        };
    }
    async #requestPreroll() {
        const { maxTotalPrerollAds, maxVideoPrerollAds } = this.#getAdsLimits();
        const adBreakSessionId = this.#adBreakSessionId;
        await this.#requestVideoPreroll(maxVideoPrerollAds);
        if (adBreakSessionId !== this.#adBreakSessionId) {
            throw new Error('Ad break interrupted by other process');
        }
        const maxAudioPrerollAds = maxTotalPrerollAds - this.#startedPrerollsCount;
        // It's OK to pass 0 to serve audio preroll because of ad reporting.
        await this.#requestAudioPreroll(maxAudioPrerollAds);
        return Boolean(this.#startedPrerollsCount);
    }
    async #requestAudioPreroll(maxAdsCount) {
        const { canShowPrerollAds } = this.#nowPlayingAdsSettings;
        this.#currentAdMedium = 'audio';
        this.#adReporter.reportAudioVideoPrerollEligibility({
            medium: 'audio',
            isPlatformEligible: canShowPrerollAds,
            isEligible: maxAdsCount > 0,
            maxRolls: maxAdsCount,
            maxTotalRolls: this.#getAdsLimits().maxTotalPrerollAds,
        });
        if (!this.#canPlayAudioPreroll || maxAdsCount <= 0) {
            return;
        }
        return this.#requestAdBreak({
            maxAdsCount,
            adType: clientAdTypes.preroll,
            medium: 'audio',
        });
    }
    async #requestVideoPreroll(maxAdsCount) {
        const { canShowVideoPrerollAds } = this.#nowPlayingAdsSettings;
        this.#currentAdMedium = 'video';
        this.#adReporter.reportAudioVideoPrerollEligibility({
            medium: 'video',
            isPlatformEligible: canShowVideoPrerollAds,
            isEligible: this.#canPlayVideoPreroll && maxAdsCount > 0,
            maxRolls: maxAdsCount,
            maxTotalRolls: this.#getAdsLimits().maxTotalPrerollAds,
        });
        if (!this.#canPlayVideoPreroll || maxAdsCount <= 0) {
            return;
        }
        /**
         * Here we are awaiting initial fetch of APS bids (called in #initializeApsBidder)
         * On later video ad requests we want to use already fetched amazon params for the GAM request.
         * Here we request the new ones but those will be used in the next video Ad within 1h period.
         */
        await this.#apsBidder.pollAndCacheVideoBids();
        return this.#requestAdBreak({
            maxAdsCount,
            adType: clientAdTypes.preroll,
            medium: 'video',
        });
    }
    #requestMidroll = async () => {
        if (!this.#canPlayMidroll) {
            return;
        }
        const playedAdsCount = await this.#requestAdBreak({
            maxAdsCount: 1,
            adType: clientAdTypes.midroll,
            medium: 'audio',
        });
        if (!playedAdsCount) {
            return this.#midrollScheduler?.start();
        }
        // TODO: expose a method on WMP that indicates if the ad player is sharing a media element with the content
        //  player so that we can conditionally call load()
        await player.load();
        if (player.isLoaded()) {
            await player.play();
        }
    };
    get canPlayClientSideAds() {
        return !api.isPremium && appSessionDataProvider.hasAdConfig;
    }
    get canPlayPreroll() {
        return this.#canPlayPreroll;
    }
    setSessionConfig(sessionConfig) {
        this.#sessionConfig = {
            ...this.#sessionConfig,
            ...sessionConfig,
        };
        this.#isFirstInSession = true;
        this.#adBreakSessionId = uuidv4();
        this.#adReporter.deregisterEvents();
        this.#midrollScheduler?.clear();
        this.#midrollScheduler = new MidrollScheduler({
            requestMidroll: this.#requestMidroll,
        });
        this.#adReporter.reportServerSidePrerollEligibility(this.#sessionConfig.prerollStream);
    }
    async requestPreroll(options) {
        const { guideId } = this.#sessionConfig;
        this.#currentAdMedium = null;
        this.#startedPrerollsCount = 0;
        player.didPlayPreroll = false;
        this.#wasAdCancelled = false;
        this.#setAdBreak(true);
        if (this.#sessionConfig?.prerollStream) {
            player.didPlayPreroll = await this.#requestServerSidePreroll();
            if (player.didPlayPreroll) {
                this.#adReporter.reportAdBreakCompleted();
            }
            if (guideId !== this.#sessionConfig.guideId) {
                return;
            }
            this.#setAdBreak(false);
            return;
        }
        this.#isReturningFromBoost = options?.isReturningFromBoost || false;
        try {
            player.didPlayPreroll = await this.#requestPreroll();
            if (player.didPlayPreroll) {
                this.#adReporter.reportAdBreakCompleted();
            }
            if (guideId !== this.#sessionConfig.guideId) {
                return;
            }
            this.#setAdBreak(false);
        }
        catch {
            // do nothing
        }
    }
    resetState() {
        this.#adBreakSessionId = null;
        player.didPlayPreroll = false;
        this.#setAdBreak(false);
    }
}
